import React, { ReactNode } from 'react';
import styled from 'styled-components';

import {
  BUTTON_TYPES,
  ELEMENT_ACTION,
  ICON_TYPES,
} from '../../shared/constants';
import {
  IContentfulLink,
  IContentfulLinkWithModal,
  IPage,
  IParentPage,
} from '../../types/shared';

import { ArrowLeft } from '../../components/atoms/icons/ArrowLeft';
import { ArrowRight } from '../../components/atoms/icons/ArrowRight';
import { ChevronLeft } from '../../components/atoms/icons/ChevronLeft';
import { ChevronRight } from '../../components/atoms/icons/ChevronRight';
import CloseIcon from '../../components/atoms/icons/CloseIcon';
import { DownloadIcon } from '../../components/atoms/icons/DownloadIcon';
import { ExternalIcon } from '../../components/atoms/icons/ExternalIcon';
import InfoIcon from '../../components/atoms/icons/InfoIcon';
import MailIcon from '../../components/atoms/icons/MailIcon';

import { ButtonLabel } from '../../styles/generic';

import { getInternalLink } from './linkGenerator';

export const getIconItem = (
  iconType: string,
  label: string,
  iconPosition: string
) => {
  const attributes = {
    className: iconPosition || 'end',
    key: `icon-${iconType}`,
  };

  let iconItem;
  switch (iconType) {
    case ICON_TYPES.none:
      break;
    case ICON_TYPES.info:
      iconItem = <InfoIcon {...attributes} />;
      break;
    case ICON_TYPES.arrowLeft:
      iconItem = <ArrowLeft {...attributes} />;
      break;
    case ICON_TYPES.close:
      iconItem = <CloseIcon {...attributes} />;
      break;
    case ICON_TYPES.mail:
      iconItem = <MailIcon {...attributes} />;
      break;
    case ICON_TYPES.file:
      iconItem = <DownloadIcon {...attributes} />;
      break;
    case ICON_TYPES.external:
      iconItem = <ExternalIcon {...attributes} />;
      break;
    case ICON_TYPES.chevronLeft:
      iconItem = (
        <ChevronLeft
          {...attributes}
          className={`${iconPosition} animate-icon-left`}
        />
      );
      break;
    case ICON_TYPES.arrow:
      iconItem = (
        <ArrowRight
          {...attributes}
          className={`${iconPosition} animate-icon`}
        />
      );
      break;
    case ICON_TYPES.internal || ICON_TYPES.chevron:
    default:
      iconItem = (
        <ChevronRight
          {...attributes}
          className={`${iconPosition} animate-icon`}
        />
      );
      break;
  }
  return iconItem;
};

export const getButtonIcon = (
  icon: string,
  type: string,
  buttonType: string
): string => {
  if (buttonType === BUTTON_TYPES.primary && type === BUTTON_TYPES.internal) {
    return icon === ICON_TYPES.default ? ICON_TYPES.arrow : icon;
  }
  if (!icon && type === BUTTON_TYPES.file) {
    return ICON_TYPES.file;
  }
  return icon === ICON_TYPES.default ? type : icon;
};

export const renderContent = (
  iconPosition: string,
  icon: string,
  type: string,
  label: string,
  buttonType: string
) => {
  const customIcon = getButtonIcon(icon, type, buttonType);

  const items: ReactNode[] = [<Text key="text">{label}</Text>];

  if (iconPosition === ICON_TYPES.start) {
    items.unshift(getIconItem(customIcon, label, iconPosition));
  } else {
    items.push(getIconItem(customIcon, label, iconPosition || ICON_TYPES.end));
  }
  return items;
};

export const getCtaLink = (
  ctaLink: IContentfulLinkWithModal | IContentfulLink
) => {
  if (!ctaLink.type || ctaLink.type === BUTTON_TYPES.action) {
    return;
  }
  if (ctaLink.type === BUTTON_TYPES.external) {
    return ctaLink?.externalUrl;
  }
  if (ctaLink.type === BUTTON_TYPES.internal) {
    return `/${getInternalLink(ctaLink.linkedPage)}`;
  }
  if (ctaLink.type === BUTTON_TYPES.file) {
    return ctaLink?.asset?.file?.url;
  }
};

export const getLinkAttributes = (
  showModal: boolean,
  to: IPage | string,
  type: string | null,
  target?: string
) => {
  const attributes: { href?: unknown; target?: string } = {};
  if (showModal || type === BUTTON_TYPES.action || !to) {
    return;
  }

  attributes.href =
    type === BUTTON_TYPES.internal
      ? `${getInternalLink(to as IParentPage)}`
      : to;

  if (target) {
    attributes.target = target;
  }

  if (type === BUTTON_TYPES.file || type === BUTTON_TYPES.external) {
    attributes.target = '_blank';
  } else if (
    type === BUTTON_TYPES.internal ||
    (typeof to === 'string' && to.includes('mailto'))
  ) {
    attributes.target = '_self';
  }

  return attributes;
};

export const getToLink = (
  showModal: boolean,
  to: IPage | string,
  type: string
) => {
  if (!showModal) {
    if (type === BUTTON_TYPES.internal) {
      return `${getInternalLink(to as IParentPage)}`;
    }
    return to;
  }
  return undefined;
};

export const getElementAction = (type: string) => {
  if (
    type === BUTTON_TYPES.internal ||
    type === BUTTON_TYPES.submit ||
    type === BUTTON_TYPES.primary
  )
    return;
  return (
    (type === BUTTON_TYPES.file && ELEMENT_ACTION.download) ||
    (type === BUTTON_TYPES.external && ELEMENT_ACTION.exit) ||
    (type === BUTTON_TYPES.action && ELEMENT_ACTION.close) ||
    type
  );
};

export const getDeqCustomItem = (
  elementStyle: string,
  showModal: boolean,
  onButtonClick: unknown,
  to: { slug?: string } | string,
  deqComponentTitle: string | null,
  type: string,
  buttonAction?: string,
  deqCustomAttributes = {}
) => {
  return !showModal
    ? JSON.stringify({
        ...{
          elementTarget: `${
            onButtonClick && !to
              ? 'click'
              : (to as { slug: string })?.slug || to
          }`,
          elementStyle,
          componentTitle: deqComponentTitle,
          elementAction: buttonAction || getElementAction(type),
        },
        ...deqCustomAttributes,
      })
    : null;
};

const Text = styled.div`
  ${ButtonLabel}
`;
